// Frontend copy of the canonical capability registry
// (services/llm-proxy/capabilities.js). CRA cannot import from services/, so
// this copy exists; test/capabilities-drift.test.mjs fails if the two lists
// differ. Entries are ONE LINE each in the exact form below (the drift test
// parses them with a regex). Pure helpers only: no Firebase in this file.

export const MODEL_IDS = ["mattergen", "fm4m", "gt4sd"];

export const CAPABILITY_GROUPS = {
  dft: 'GPAW DFT',
  qe: 'Quantum ESPRESSO',
  lattice: 'Lattice generation',
  mattergen: 'MatterGen',
  gt4sd: 'GT4SD',
  fm4m: 'IBM FM4M',
  jobs: 'Jobs',
  reports: 'Reports',
};

export const CAPABILITIES = [
  { id: "dft.singlepoint", service: "dft", label: "GPAW single point energy", description: "Spin-polarized SCF at fixed geometry: energy, Fermi level, coarse band gap.", cost_hint: "heavy" },
  { id: "dft.relax", service: "dft", label: "GPAW geometry relaxation", description: "Relax atomic positions and cell with GPAW.", cost_hint: "heavy" },
  { id: "dft.elastic", service: "dft", label: "GPAW bulk modulus", description: "Bulk modulus from a volume scan.", cost_hint: "heavy" },
  { id: "dft.elastic_tensor", service: "dft", label: "Elastic tensor (DFT)", description: "Full 6x6 elastic tensor from 12 finite-strain deformations. Hours per candidate.", cost_hint: "heavy" },
  { id: "dft.dos", service: "dft", label: "GPAW density of states", description: "Total, element-projected, and orbital-projected DOS.", cost_hint: "heavy" },
  { id: "dft.bandstructure", service: "dft", label: "GPAW band structure", description: "Band structure along a high-symmetry k-path.", cost_hint: "heavy" },
  { id: "dft.spin_density", service: "dft", label: "GPAW spin density", description: "Spin density and per-site magnetic moments.", cost_hint: "heavy" },
  { id: "dft.optical", service: "dft", label: "GPAW optical properties", description: "Dielectric function and absorption spectrum.", cost_hint: "heavy" },
  { id: "dft.effective_mass", service: "dft", label: "GPAW effective masses", description: "Carrier effective masses from band curvature.", cost_hint: "heavy" },
  { id: "qe.singlepoint", service: "qe", label: "QE single point energy", description: "Quantum ESPRESSO SCF at fixed geometry.", cost_hint: "heavy" },
  { id: "qe.relax", service: "qe", label: "QE geometry relaxation", description: "Relax atomic positions and cell with Quantum ESPRESSO.", cost_hint: "heavy" },
  { id: "qe.elastic", service: "qe", label: "QE bulk modulus", description: "Bulk modulus from a Quantum ESPRESSO volume scan.", cost_hint: "heavy" },
  { id: "qe.dos", service: "qe", label: "QE density of states", description: "Density of states via dos.x.", cost_hint: "heavy" },
  { id: "qe.bandstructure", service: "qe", label: "QE band structure", description: "Band structure via bands.x along a high-symmetry k-path.", cost_hint: "heavy" },
  { id: "qe.optical", service: "qe", label: "QE optical properties", description: "Dielectric function via epsilon.x.", cost_hint: "heavy" },
  { id: "qe.effective_mass", service: "qe", label: "QE effective masses", description: "Carrier effective masses from Quantum ESPRESSO band curvature.", cost_hint: "heavy" },
  { id: "qe.phonon", service: "qe", label: "QE phonon dispersion", description: "Phonon dispersion via ph.x and matdyn.x.", cost_hint: "heavy" },
  { id: "qe.thermal", service: "qe", label: "QE thermal properties", description: "Thermal properties from the phonon density of states.", cost_hint: "heavy" },
  { id: "lattice.generate", service: "lattice", label: "Lattice generation", description: "Objective-driven lattice GA run on the lattice service.", cost_hint: "heavy" },
  { id: "mattergen.generate", service: "mattergen", label: "MatterGen generation", description: "Inorganic crystal generation on the MatterGen GPU service.", cost_hint: "heavy" },
  { id: "gt4sd.generate", service: "gt4sd", label: "GT4SD generation", description: "Small-molecule generation (HuggingFace or Regression Transformer).", cost_hint: "heavy" },
  { id: "fm4m.representation", service: "fm4m", label: "FM4M jobs (generation and embeddings)", description: "Polymer and molecule generation plus molecular embeddings on the IBM FM4M service.", cost_hint: "light" },
  { id: "jobs.cancel.own", service: "jobs", label: "Cancel your own jobs", description: "Stop a job you submitted yourself.", cost_hint: "light" },
  { id: "jobs.cancel.org", service: "jobs", label: "Cancel any job in your organization", description: "Stop a job a teammate submitted.", cost_hint: "light" },
  { id: "jobs.view.org", service: "jobs", label: "See teammates' jobs", description: "Denied means you see only the jobs you submitted.", cost_hint: "light" },
  { id: "reports.patent_packet", service: "reports", label: "Patent packet report", description: "Multi-call LLM patent disclosure packet.", cost_hint: "light" },
];

export const CAPABILITY_IDS = Object.freeze(CAPABILITIES.map((c) => c.id));
const BY_ID = new Map(CAPABILITIES.map((c) => [c.id, c]));
export const WEIGHT_MIN = 0;
export const WEIGHT_MAX = 2;

export function capabilityById(id) {
  return BY_ID.get(id) || null;
}

/** AppShell analysis kind ('dft_relax', 'qe_phonon', 'patent_packet') -> capability id, or null when ungated. */
export function capabilityForAnalysisKind(kind) {
  if (kind === 'patent_packet') return 'reports.patent_packet';
  const m = /^(dft|qe)_(.+)$/.exec(String(kind || ''));
  if (!m) return null;
  const id = `${m[1]}.${m[2]}`;
  return BY_ID.has(id) ? id : null;
}

function has(obj, key) {
  return !!obj && Object.prototype.hasOwnProperty.call(obj, key);
}

/** Effective permission set. Absent = allowed; member override beats org default. */
export function resolveEffectiveCapabilities({ defaults = {}, overrides = {}, superadmin = false } = {}) {
  const denied = new Set();
  if (superadmin) return { denied };
  for (const id of CAPABILITY_IDS) {
    const o = has(overrides, id) ? overrides[id] : undefined;
    const d = has(defaults, id) ? defaults[id] : undefined;
    const allowed = typeof o === 'boolean' ? o : (typeof d === 'boolean' ? d : true);
    if (!allowed) denied.add(id);
  }
  return { denied };
}

/** Full policy for every MODEL_IDS entry; absent/invalid pieces fall back to enabled at weight 1. */
export function normalizeModelPolicy(raw) {
  const src = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  const out = {};
  for (const id of MODEL_IDS) {
    const e = src[id] && typeof src[id] === 'object' ? src[id] : {};
    const w = typeof e.weight === 'number' && Number.isFinite(e.weight) ? e.weight : 1;
    out[id] = {
      enabled: typeof e.enabled === 'boolean' ? e.enabled : true,
      weight: Math.max(WEIGHT_MIN, Math.min(WEIGHT_MAX, w)),
    };
  }
  return out;
}

/** [{ group, label, items }] in CAPABILITY_GROUPS order, for the Admin Portal tree. */
export function groupedCapabilities() {
  return Object.entries(CAPABILITY_GROUPS)
    .map(([group, label]) => ({ group, label, items: CAPABILITIES.filter((c) => c.service === group) }))
    .filter((g) => g.items.length > 0);
}
